import { useMemo, useState } from 'react';
import { Button, StatusMessage } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Loader2, SearchX, Building2 } from 'lucide-react';
import WorkspaceHeroCard from './WorkspaceHeroCard.jsx';
import WorkspaceCompactCard from './WorkspaceCompactCard.jsx';
import WorkspaceListSkeleton from './WorkspaceListSkeleton.jsx';

const COLLAPSED_COUNT = 5;

/**
 * Renders the active workspace hero card and the collapsible "OTHER
 * WORKSPACES" list. Search/sort are applied upstream (CT-4938/CT-4939) — this
 * component just lays out whatever active/others it's handed.
 *
 * "Show N more" does two jobs, in order: first it reveals the rows already
 * fetched beyond the collapsed limit, then — once those are all visible and the
 * API says there are further pages — it calls `loadMore()` to append the next
 * one. Without that second step the button could only ever reach the first page
 * (15 workspaces), leaving the rest unreachable on this screen.
 */
function WorkspaceList({
  active,
  others,
  metaById,
  loading,
  isLoadingMore,
  hasMore,
  loadMore,
  totalCount,
  hasQuery = false,
  switchingId,
  onSelect,
  onOpenStudio,
}) {
  const { t } = useTranslation('core-shell');
  const [expanded, setExpanded] = useState(false);

  // A new query is a new list: staying expanded left a "Show less" button
  // sitting under a handful of results with nothing collapsed behind it.
  // Adjusted while rendering rather than in an effect, so the collapse lands in
  // the same paint as the new results instead of one frame after them.
  const [lastHasQuery, setLastHasQuery] = useState(hasQuery);
  if (lastHasQuery !== hasQuery) {
    setLastHasQuery(hasQuery);
    setExpanded(false);
  }

  // Names repeat in real data (three workspaces called "Testing tenant" in
  // dev), which left several identically-labelled buttons for a screen
  // reader. Only the ambiguous ones get their slug appended.
  const duplicateNames = useMemo(() => {
    const seen = new Set();
    const dupes = new Set();
    [active, ...others].forEach((ws) => {
      if (!ws) return;
      if (seen.has(ws.name)) dupes.add(ws.name);
      else seen.add(ws.name);
    });
    return dupes;
  }, [active, others]);

  if (loading) return <WorkspaceListSkeleton />;

  // A switch is in flight somewhere. `openWorkspace` already drops clicks while
  // one is running; this makes every other card LOOK inert instead of silently
  // swallowing the press (CT-4940 asks for "disable further clicks").
  const anySwitching = switchingId != null;

  if (!active && others.length === 0) {
    // Two distinct empty states: "your search matched nothing" is recoverable
    // (clear the query); "you have no workspaces at all" is not, and telling
    // someone to try another search term there would be nonsense.
    const empty = hasQuery
      ? { icon: SearchX, title: 'workspaceSelector.noResults', subtitle: 'workspaceSelector.noResultsHint' }
      : { icon: Building2, title: 'workspaceSelector.noWorkspaces', subtitle: 'workspaceSelector.noWorkspacesHint' };
    return (
      <div className="rounded-2xl border border-edge bg-surface/40">
        <StatusMessage icon={empty.icon} title={t(empty.title)} subtitle={t(empty.subtitle)} />
      </div>
    );
  }

  const visibleOthers = expanded ? others : others.slice(0, COLLAPSED_COUNT);
  // Remaining = rows fetched but hidden by the collapse, plus whatever the
  // server still has on later pages. `totalCount` is the total for the CURRENT
  // query (the search is server-side, so it paginates the matches), which
  // makes this correct with and without a query alike.
  const totalOthers = Math.max(others.length, (totalCount || 0) - (active ? 1 : 0));
  const remaining = Math.max(0, totalOthers - visibleOthers.length);
  const canShowMore = remaining > 0 && (!expanded || hasMore);

  return (
    <div className="flex flex-col gap-3">
      {active && (
        <WorkspaceHeroCard
          workspace={active}
          meta={metaById[active.id]}
          disambiguator={duplicateNames.has(active.name) ? active.slug : undefined}
          switching={switchingId === active.id}
          disabled={anySwitching && switchingId !== active.id}
          onSelect={onSelect}
          onOpenStudio={onOpenStudio}
        />
      )}

      {others.length > 0 && (
        <>
          {/* Only when there IS an active card above for these to be "other"
              than — under a query that didn't match it, there is no "this". */}
          {active && (
            <p className="mt-4 text-[11px] font-semibold uppercase tracking-wider text-content-faint">
              {t('workspaceSelector.otherWorkspaces')}
            </p>
          )}
          {visibleOthers.map((ws) => (
            <WorkspaceCompactCard
              key={ws.id}
              workspace={ws}
              meta={metaById[ws.id]}
              disambiguator={duplicateNames.has(ws.name) ? ws.slug : undefined}
              switching={switchingId === ws.id}
              disabled={anySwitching && switchingId !== ws.id}
              onSelect={onSelect}
              onOpenStudio={onOpenStudio}
            />
          ))}

          {(canShowMore || (expanded && others.length > COLLAPSED_COUNT)) && (
            <div className="mt-1 flex justify-center">
              {canShowMore ? (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={isLoadingMore}
                  onClick={() => (expanded ? loadMore?.() : setExpanded(true))}
                >
                  {isLoadingMore && <Loader2 size={14} className="mr-1.5 animate-spin" />}
                  {t('workspaceSelector.showMore', { count: remaining })}
                </Button>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => setExpanded(false)}>
                  {t('workspaceSelector.showLess')}
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default WorkspaceList;
